import { createFileRoute, Outlet, useChildMatches, useNavigate } from '@tanstack/react-router'
import { type FeedbackFilters, feedbackSearchSchema } from '@/features/feedback/api'
import { FeedbackInboxScreen } from '@/features/feedback/feedback-inbox'

/**
 * `/feedback` is the inbox; `/feedback/$reportId` is one report's detail
 * (design spec §5).
 *
 * A parent route whose component never renders `<Outlet />` silently swallows
 * its children — the URL matches, and the parent's own screen shows instead.
 * Rather than splitting this file into an index sibling (which would rename a
 * tracked file), it does what `apps/admin`'s `groups.tsx` does: render the
 * inbox only when no child has matched, and let the router fill in the detail
 * below otherwise.
 *
 * `validateSearch` puts the status, type, company and page filters in the URL:
 * a filtered or paginated view is linkable and survives a reload (AGENTS.md).
 */
export const Route = createFileRoute('/_authenticated/feedback')({
  validateSearch: feedbackSearchSchema,
  component: FeedbackRoute,
})

function FeedbackRoute() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const children = useChildMatches()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <FeedbackInboxScreen
      search={search}
      onSearchChange={(patch: Partial<FeedbackFilters>) => {
        void navigate({
          to: '/feedback',
          search: { ...search, ...patch },
          replace: true,
        })
      }}
    />
  )
}
